import { FormControl } from "@material-ui/core";
import React, { useMemo } from "react";
import { ValueType } from "react-select/src/types";

import { UserLevel } from "../../../enums/userLevel";
import { useAuth } from "../../../features/auth/context";
import { DialogSelect } from "../../shared/dialogSelect";

interface Props {
    value: UserLevel | null;
    onChange: (value: UserLevel) => void;
    disabled?: boolean;
}

export function UserLevelSelector({
    value,
    onChange,
    disabled = false,
}: Props) {
    const {
        isGlobalAdmin,
        isDealerAdmin,
        isOrgAdmin,
        isSiteAdmin,
        isSiteEmployee,
    } = useAuth();

    const options: { label: string; value: string; }[] = useMemo(() => {
        const userLevels = getAvailableUserLevels(
            isGlobalAdmin,
            isDealerAdmin,
            isOrgAdmin,
            isSiteAdmin,
            isSiteEmployee,
        );
        return userLevels.map((it) => ({ label: it, value: it }));
    }, [
        isGlobalAdmin,
        isDealerAdmin,
        isOrgAdmin,
        isSiteAdmin,
        isSiteEmployee,
    ]);

    return (
        <FormControl style={{ minWidth: "300px" }}>
            <DialogSelect
                options={options}
                value={{ value: value || "", label: value || "" }}
                onChange={(
                    value: ValueType<{ value: string; label: string }>
                ) => {
                    const val = value as { value: string; label: string };
                    if (!val) {
                        return;
                    }
                    onChange(val.value as UserLevel)
                }}
                isDisabled={disabled}
            />
        </FormControl>
    );
}

function getAvailableUserLevels(
    isGlobalAdmin: boolean,
    isDealerAdmin: boolean,
    isOrganizationAdmin: boolean,
    isSiteAdmin: boolean,
    isSiteEmployee: boolean,
): UserLevel[] {
    const roles: { userLevel: UserLevel; hasAccess: boolean }[] = [
        { userLevel: UserLevel.GlobalAdmin, hasAccess: isGlobalAdmin },
        { userLevel: UserLevel.DealerAdmin, hasAccess: isDealerAdmin },
        { userLevel: UserLevel.DealerEmployee, hasAccess: isDealerAdmin },
        { userLevel: UserLevel.OrgAdmin, hasAccess: isOrganizationAdmin },
        { userLevel: UserLevel.OrgEmployee, hasAccess: isOrganizationAdmin },
        { userLevel: UserLevel.SiteAdmin, hasAccess: isSiteAdmin },
        { userLevel: UserLevel.SiteEmployee, hasAccess: isSiteEmployee },
    ];

    const firstAccessibleIndex = roles.findIndex(role => role.hasAccess);
    if (firstAccessibleIndex === -1) return [];

    return roles
        .slice(firstAccessibleIndex)
        .map(role => role.userLevel);
}
